import { useEffect, useState, useRef } from "react";
import { useAuthStore } from "@/store/authStore";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { StoreList } from "@/components/stores/StoreList";
import { CreateStoreWizard } from "@/components/stores/CreateStoreWizard";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StoreEmployees } from "@/components/stores/StoreEmployees";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { AddStoreDialog } from "@/components/stores/AddStoreDialog";
import { isAdminRole, isSuperAdmin } from "@/utils/roleUtils";
import { useTranslation } from "react-i18next";
import { CreateStoreWizardFormValues } from "@/components/stores/wizard/types";
import { BusinessType } from "@/models/storeModel";

export default function StoreManagement() {
  const { t } = useTranslation();

  const { user, refreshStoreAccess } = useAuthStore();
  const { stores, fetchStores, loading, error, addStore } =
    useStoreManagementStore();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const { toast } = useToast();
  const initialLoadComplete = useRef(false);

  useEffect(() => {
    // Only run this effect once to avoid infinite loops
    if (initialLoadComplete.current) return;

    const loadStores = async () => {
      try {
        if (user?.id) {
          await refreshStoreAccess(user.id);
          await fetchStores();
        }
        initialLoadComplete.current = true;
      } catch (error) {
        toast({
          title: t("error"),
          description: t("failedToFetchStores"),
          variant: "destructive",
        });
      }
    };

    loadStores();
    return () => {
      initialLoadComplete.current = false;
    };
  }, [user?.id]);

  // Check if user has admin permissions
  const hasAdminPermission = isAdminRole(user?.role);
  const isSuperAdminUser = isSuperAdmin(user?.role);

  const userStores = stores;
  const handleCreateStore = () => {
    setShowCreateWizard(true);
  };

  const handleCreateStoreComplete = async (
    formValues: CreateStoreWizardFormValues
  ) => {
    try {
      if (!user?.id) {
        throw new Error("No authenticated user found");
      }

      await addStore({
        name: formValues.name,
        address: `${formValues.street}, ${formValues.streetNumber}, ${
          formValues.zip
        } ${formValues.city}, ${formValues.state || "Germany"}`,
        phone: formValues.phone,
        email: formValues.email,
        isB2B: formValues.isB2B ?? true,
        legalName: formValues.legalName,
        taxId: formValues.taxId,
        businessType: formValues.businessType as BusinessType,
        registrationNumber: formValues.registrationNumber,
        industryType: formValues.industryType,
        companyPhone: formValues.companyPhone,
        is_active: true,
      });

      await fetchStores();
      setShowCreateWizard(false);

      toast({
        title: t("success"),
        description: t("storeCreatedSuccessfully"),
      });
    } catch (error) {
      console.error("Error creating store:", error);
      toast({
        title: t("error"),
        description: t("failedToCreateStore"),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("storeManagement.title")}
          </h1>
          <p className="text-muted-foreground mt-2">
            {hasAdminPermission
              ? t("storeManagement.manageAllStores")
              : t("storeManagement.viewAndManageAssignedStores")}
          </p>
        </div>

        {/* Make Create Store button more prominent and always visible for manager users */}
        {hasAdminPermission && (
          <div className="flex gap-2">
            <Button
              onClick={() => handleCreateStore()}
              className="bg-primary hover:bg-primary/90"
              size="lg"
            >
              <Plus className="mr-2 h-4 w-4" />
              {t("storeManagement.createStore")}
            </Button>
          </div>
        )}
      </div>

      <Separator />

      {/* Add quick action button if no stores exist */}
      {userStores.length === 0 && hasAdminPermission && (
        <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg bg-muted/50">
          <h3 className="text-xl font-medium mb-2">
            {t("storeManagement.noStoresYet")}
          </h3>
          <p className="text-muted-foreground mb-6 text-center">
            {t("storeManagement.createFirstStore")}
          </p>
          <div className="flex gap-4">
            <Button
              size="lg"
              variant="outline"
              onClick={() => handleCreateStore()}
            >
              {t("storeManagement.fullStoreSetup")}
            </Button>
          </div>
        </div>
      )}

      <Tabs defaultValue="stores" className="w-full">
        <TabsList>
          <TabsTrigger value="stores">{t("stores.title")}</TabsTrigger>
          {selectedStoreId && (
            <TabsTrigger value="employees">
              {t("storeManagement.storeEmployees")}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="stores" className="mt-4">
          <StoreList
            stores={userStores}
            isLoading={loading}
            isAdminHQ={hasAdminPermission}
            onSelectStore={(storeId) => setSelectedStoreId(storeId)}
          />
        </TabsContent>

        <TabsContent value="employees" className="mt-4">
          {selectedStoreId && (
            <StoreEmployees
              storeId={selectedStoreId}
              isAdminHQ={hasAdminPermission}
              isStoreAdmin={user?.role === "admin"}
            />
          )}
        </TabsContent>
      </Tabs>

      {showCreateWizard && (
        <CreateStoreWizard
          open={showCreateWizard}
          onOpenChange={setShowCreateWizard}
          onComplete={handleCreateStoreComplete}
        />
      )}

      {showCreateDialog && (
        <AddStoreDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
        />
      )}
    </div>
  );
}
